<script lang="ts" setup>
import { toTypedSchema } from '@vee-validate/zod';
import { readContract } from '@wagmi/core';
import { useAccount, useBalance } from '@wagmi/vue';
import dayjs from 'dayjs';
import { useField, useForm } from 'vee-validate';
import { type Address, formatUnits } from 'viem';
import * as zod from 'zod';

import { formatCurrency, formatNumber, isValidNumber, toShortAddress } from '@/utils/helpers';
import { useNuxtApp } from '#app';
import { computed, ref, useEventModalsStore, useProviderConfig } from '#imports';
import type { CollectionApprovedTokens } from '~/service/api/types';
import { EvmItemType } from '~/service/EVM/types';
import { isEvmErc721 } from '~/service/utils';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useUserStore } from '~/stores/user/useUserStore';
import { ScopeWagmiKeys } from '~/types';

const calendar = ref<HTMLElement | null>(null);
const isDatePickerVisible = ref(false);
const dateInput = ref<HTMLElement | null>(null);
const overlay = ref<HTMLElement | null>(null);

const props = defineProps<{
    data: Pick<AppWTFNft, 'name' | 'uuid' | 'address' | 'price' | 'network' | 'identifier' | 'displayImageURL' | 'itemType'> & { slug: string };
    modelValue: boolean;
    fees: AppWTFCollectionFees;
}>();

const { collection, QueryEvmKey } = useEVMApi();

const userStore = useUserStore();
const modalEventsStore = useEventModalsStore();
const { eventEmitter } = useEVMCore();

const { user } = storeToRefs(userStore);
const alertStore = useAlertsStore();

const {
    data: commissions,
    status: statusCommissions
} = useAsyncData(
    `${props.data.slug}-${QueryEvmKey.COMMISSION_BY_SLUG}`,
    async () => {
        const { data } = await collection.commissionById(props.data.slug);
        return data;
    },
    {
        default: () => []
    }
);

const { data: approvedErc20Tokens, status: statusApprovedErc20Tokens } = useAsyncData(
    `${props.data.slug}-${QueryEvmKey.COLLECTION_APPROVED_TOKENS}`,
    async () => {
        if (props.data.network) {
            const { data } = await collection.approvedErc20Tokens(props.data.network);
            return data;
        }
        return [];
    },
    {
        default: () => [],
        transform: data => data.length ? data.filter(item => item.itemType === EvmItemType.ERC20) : []
    }
);

const requiredCommissionPercent = computed(() => commissions.value
    ? commissions.value
        .filter(item => item.required)
        .reduce((sum, item) => sum + item.fee, 0) * 100
    : null);

const handleSelectedDateTimeUpdate = (dateTime: string) => {
    setValue(dateTime);
};

const formattedSelectedDate = computed(() => {
    if (isDatePickerVisible.value) {
        return dayjs(durationValue.value).format('DD.MM.YYYY ');
    }
    else {
        return dayjs(durationValue.value).format('MMM DD ');
    }
});

const formattedSelectedTime = computed(() => {
    if (isDatePickerVisible.value) {
        return dayjs(durationValue.value).format('HH:mm');
    }
    else {
        return dayjs(durationValue.value).format('hh:mm A');
    }
});

const toggleDatePicker = () => {
    isDatePickerVisible.value = !isDatePickerVisible.value;
};

const durations = [
    {
        name: '1 Day',
        value: dayjs().add(
            1,
            'day'
        )
            .format()
    },
    {
        name: '1 Week',
        value: dayjs().add(
            1,
            'week'
        )
            .format()
    },
    {
        name: '1 Month',
        value: dayjs().add(
            1,
            'month'
        )
            .format()
    }
];

const {
    contract: { wrap },
    constants: { NETWORK_NAME },
    map: { chainMap },
    utils: { isEvmErc1155 }
} = useProviderConfig()(['contract', 'constants', 'map', 'utils']);

const emit = defineEmits<{
    (e: 'update:modelValue', payload: boolean): void;
}>();

const { chainId } = useAccount();

const { data: myBalance } = useBalance({
    chainId,
    address: user.value || '0x',
    scopeKey: ScopeWagmiKeys.USE_BALANCE,
    query: { enabled: !!user.value }
});

const { $provider } = useNuxtApp();

const previousWETHBalance = ref<Nullable<string>>(null);

const minValue = computed(() => ({
    [NETWORK_NAME.MAINNET]: {
        precision: 4
    },
    [NETWORK_NAME.POLYGON]: {
        precision: 6
    },
    [NETWORK_NAME.XSOLLA_ZK]: {
        precision: 4
    }
}));

const validationSchema
    = zod.object({
        price: zod
            .preprocess(
                val => (typeof val === 'number' ? val.toString() : val),
                zod
                    .string()
                    .refine(
                        val => isValidNumber(val),
                        { message: 'Invalid number format' }
                    )
                    .transform(val => Number(val))
                    // .refine(
                    //     val => minValue.value[props.data.network].price <= val,
                    //     {
                    //         message: `Offer must be at least the minimum price per unit of ${minValue.value[props.data.network].usd} USD (${minValue.value[props.data.network].price} ETH)`
                    //     }
                    // )
            ),
        duration: zod.string().nullable(),
        currency: zod.object({
            address: zod.string(),
            decimals: zod.number(),
            itemType: zod.number(),
            symbol: zod.string(),
            minPrice: zod.number()
        }).nullable(),
        amount: zod
            .preprocess(
                val => (typeof val === 'number' ? val.toString() : val),
                zod
                    .string()
                    .optional()
                    .refine(
                        val => val && isValidNumber(val),
                        { message: 'Invalid number format' }
                    )
                    .transform(val => Number(val))
                    .refine(
                        val => val >= 0 && (/^-?\d+$/).test(String(val)),
                        { message: 'Invalid value' }
                    )
            )
    }).superRefine((data, ctx) => {
        if (data.currency && data.price < data.currency.minPrice) {
            ctx.addIssue({
                code: zod.ZodIssueCode.custom,
                path: ['price'],
                message: `Offer must be at least the minimum price of ${data.currency.minPrice} ${data.currency.symbol}`
            });
        }
    });

type FormValuesType = zod.infer<typeof validationSchema>;

const {
    handleSubmit,
    values,
    meta,
    setFieldValue
} = useForm<FormValuesType>({
    validationSchema: toTypedSchema(validationSchema),
    initialValues: {
        amount: 1,
        currency: null
    }
});

const { 'value': priceValue, 'errorMessage': priceValueError } = useField<string>('price');

const { 'value': amountValue, 'errorMessage': amountValueError } = useField<string>('amount');

const { value: currencyValue } = useField<CollectionApprovedTokens>('currency');

watch([approvedErc20Tokens], ([newApprovedErc20Tokens]) => {
    if (newApprovedErc20Tokens.length && newApprovedErc20Tokens[0]) {
        setFieldValue('currency', newApprovedErc20Tokens[0]);
    }
});

const {
    data: marketContract,
    status: marketContractsStatus
} = useAsyncData(
    QueryEvmKey.MARKETPLACE_CONTRACTS,
    async () => {
        const { data } = await collection.contractSettingsList();
        return data;
    },
    {
        transform: data => data.find(item => props.data.network === item.network),
        default: () => null
    }
);

const {
    status: wethBalanceStatus,
    data: wethBalance,
    refresh
} = useAsyncData(`${QueryEvmKey.WETH_BALANCE}-${props.data.uuid}`, async () => {
    if (props.data.network && chainMap[props.data.network] && user.value && $provider.config && currencyValue.value) {
        // const abi = props.content.

        const data = await readContract(
            $provider.config,
            {
                address: currencyValue.value.address,
                functionName: 'balanceOf',
                chainId: chainMap[props.data.network].id,
                abi: wrap.weth[chainMap[props.data.network].id].abi,
                args: [user.value || '0x']
            }
        );
        const readableValue = formatUnits(
            data,
            currencyValue.value.decimals
        );

        return parseFloat(readableValue).toFixed(4)
            .replace(
                /\.?0+$/,
                ''
            );
    }
    return undefined;
}, {
    watch: [currencyValue, user]
});

const {
    'value': durationValue,
    setValue,
    'errorMessage': durationValueError
} = useField<string>('duration');

const balanceFormatted = computed(() => myBalance.value && formatCurrency({
    value: myBalance.value.value,
    currencyType: myBalance.value.symbol as User['network']
}));

const { makeOffer } = useMakeOffer(props.data.uuid);

const initiatorKey = props.data.uuid;

const isLoading = useState(initiatorKey, () => false);

const buttonLoading = computed(() => isLoading.value || statusCommissions.value === 'pending' || marketContractsStatus.value === 'pending');

const onSubmit = handleSubmit(async ({ price, duration, amount, currency }) => {
    if (!requiredCommissionPercent.value || !marketContract.value) {
        return alertStore.add({
            critical: true,
            description: `It is not possible to create an order now, try again later`,
            status: 'type.error',
            title: 'Something went wrong'
        });
    }

    if (duration && requiredCommissionPercent.value && commissions.value && currency) {
        await makeOffer({
            erc20TokenAddress: currency.address as Address,
            paramNetwork: props.data.network,
            identifier: props.data.identifier,
            contract: props.data.address,
            marketplaceContract: marketContract.value,
            value: amount
                ? price * amount
                : price,
            endTime: duration,
            marketplace: 'default',
            fees: commissions.value.map(item => ({
                recipient: item.address,
                fee: item.fee * 100,
                required: item.required
            })),
            nftItemType: props.data.itemType,
            itemType: currency.itemType,
            ...(amount
                ? { amount: Number(amount) }
                : {})
        });
    }
});

const sumFees = computed(() => {
    if (requiredCommissionPercent.value && values.price) {
        const amount = values.amount || 1;
        const userPrice = values.price * amount;

        const fee = userPrice * (requiredCommissionPercent.value / 100);

        return formatNumber(fee, minValue.value[props.data.network].precision);
    }
    return null;
});

const totalPrice = computed(() => {
    if (requiredCommissionPercent.value && values.price) {
        const amount = values.amount || 1;
        const userPrice = values.price * amount;

        const priceAfterFees = userPrice * (1 - (requiredCommissionPercent.value / 100));

        return formatNumber(priceAfterFees, minValue.value[props.data.network].precision);
    }
    return null;
});

const symbolCurrency = computed(() => {
    return currencyValue.value?.symbol || '-';
});

const calendarStyle = computed(() => {
    if (dateInput.value && overlay.value && calendar.value) {
        const dateInputRect = dateInput.value.getBoundingClientRect();
        const overlayRect = overlay.value.getBoundingClientRect();

        const offsetX = overlayRect.right - dateInputRect.right;
        const offsetY = dateInputRect.top - overlayRect.top;

        const calendarElHeight = 453;

        return {
            top: `${offsetY - calendarElHeight - 4}px`,
            right: `${offsetX}px`
        };
    }
    return null;
});

const getDescriptionForModal = (itemType: EvmItemType) => {
    let result: string[];
    result = [];
    if (isEvmErc721(itemType)) {
        result = [`${totalPrice.value} ${currencyValue.value?.symbol} Chain: ${props.data.network}`];
    }
    else if (isEvmErc1155(itemType)) {
        result = [`${values.amount} ${values.amount > 1 ? 'items' : 'item'} ${totalPrice.value} ${currencyValue.value?.symbol}`, `Chain: ${props.data.network}`];
    }

    return result;
};

onMounted(() => {
    eventEmitter.on('buildOffer', {
        key: initiatorKey,
        onSuccess: () => {
            modalEventsStore.removeByType('approve');

            isLoading.value = false;
            emit(
                'update:modelValue',
                false
            );

            alertStore.add({
                title: 'Offer made successfully!',
                description: ``,
                critical: false,
                status: 'type.success'
            });
        },
        onError: () => {
            isLoading.value = false;
            modalEventsStore.add(
                {
                    status: '',
                    type: 'error',
                    images: [props.data.displayImageURL ? props.data.displayImageURL : ''],
                    imageState: 'error',
                    title: `${props.data?.name}`,
                    description: getDescriptionForModal(props.data.itemType),
                    heading: 'Error',
                    itemType: props.data.itemType,
                    content: 'Something went wrong, Try again later.'
                });
        },
        onLoad: (loading) => {
            isLoading.value = loading;
            if (loading) {
                modalEventsStore.add(
                    {
                        status: '',
                        type: 'approve',
                        images: [props.data.displayImageURL ? props.data.displayImageURL : ''],
                        imageState: 'loading',
                        title: `${props.data?.name}`,
                        description: getDescriptionForModal(props.data.itemType),
                        heading: 'Approve offer',
                        itemType: props.data.itemType,
                        content: 'You\'ll be asked to review and confirm this offer from your wallet.'
                    });
            }
        }
    });
});

onUnmounted(() => {
    eventEmitter.off('buildOffer', initiatorKey);
});

</script>

<template>
  <form class="display-flex flex-column align-items-start position-relative width-full modal"
        @submit.prevent="onSubmit"
  >
    <h2 class="display-800-accent">
      Make offer
    </h2>

    <div class="display-flex gap-16 align-items-center width-full block-pad">
      <ImgMain :alt="data.price?.token"
               :src="data.displayImageURL"
               class="offer-token"
      />

      <div class="display-flex flex-column align-items-center width-full gap-32 ">
        <div class="display-flex align-items-center justify-between width-full">
          <p class="compact-400-accent color-text-grey">
            Floor price
          </p>
          <p class="compact-400-numeric color-text-grey">
            -
          </p>
        </div>

        <div class="display-flex align-items-center justify-between width-full">
          <p class="compact-400-accent color-text-grey">
            Best offer
          </p>
          <p class="compact-400-numeric color-text-grey">
            -
          </p>
        </div>
      </div>
    </div>

    <div class="modal-content">
      <div class="display-flex flex-column width-full gap-32 border-block">
        <div class="display-flex justify-between align-items-center">
          <div v-if="userStore.network"
               class="display-flex gap-16 align-items-center"
          >
            <div class="display-flex align-items-center justify-center wallet-offer">
              <IconMain :icon="userStore.network?.toUpperCase()"
                        :size="24"
              />
            </div>
            <div v-if="userStore.network">
              <p class="compact-600-accent">
                {{
                  chainMap[userStore.network].name
                }}
              </p>
              <p class="text-500-numeric color-text-grey">
                {{ toShortAddress(userStore.user!) }}
              </p>
            </div>
          </div>

          <div class="display-flex flex-column align-items-end">
            <div class="display-flex flex-row w-balance">
              <LoaderDots v-if="wethBalanceStatus === 'pending' && (!previousWETHBalance || previousWETHBalance !== wethBalance)"
                          :height="24"
                          :width="60"
                          class="mr-8"
              />
              <span v-else-if="wethBalance"
                    class="compact-600-accent"
              >{{ wethBalance }} {{currencyValue.symbol}}</span>
            </div>

            <span class="compact-600-accent text-end">
              {{ balanceFormatted }} {{ userStore.network?.toUpperCase() }}
            </span>
          </div>
        </div>

        <div class="position-relative">
          <div class="display-flex align-items-center width-full gap-4">
            <input v-model="priceValue"
                   :step="`${currencyValue?.minPrice || 0}`"
                   aria-label="price"
                   class="base-input compact-400-default ml-2"
                   inputmode="decimal"
                   name="price"
                   placeholder="Price"
                   type="number"
            >
            <LoaderDots v-if="statusApprovedErc20Tokens === 'pending'" />
            <FormSelect v-else-if="approvedErc20Tokens"
                        :initial-value="currencyValue"
                        :is-disabled="approvedErc20Tokens.length <= 1"
                        :options="approvedErc20Tokens"
                        container-class="currency-select"
                        label="symbol"
                        track-by="symbol"
                        @on-change="val => setFieldValue('currency', val)"
            />
          </div>
          <Transition mode="out-in"
                      name="fade"
          >
            <span v-if="priceValueError"
                  class="position-absolute compact-300-accent price-error"
            >{{ priceValueError }}</span>
          </Transition>
        </div>

        <div v-if="isEvmErc1155(data.itemType)"
             class="display-flex flex-column width-full gap-4 position-relative"
        >
          <p class="compact-500-accent mb-4">
            Quantity of items
          </p>
          <input v-model="amountValue"
                 aria-label="Quantity of items"
                 class="base-input compact-400-default ml-2"
                 name="price"
                 placeholder="Enter a quantity"
                 type="number"
          >
          <Transition mode="out-in"
                      name="fade"
          >
            <span v-if="amountValueError"
                  class="position-absolute compact-300-accent price-error"
            >{{ amountValueError }}</span>
          </Transition>
        </div>

        <div class="display-flex flex-column width-full gap-16">
          <p class="compact-500-accent">
            Duration
          </p>

          <div class="display-flex align-items-center justify-between">
            <FormSelect :error="durationValueError"
                        :options="durations"
                        container-class="duration-select"
                        label="name"
                        track-by="name"
                        @on-change="({ value }) => setValue(value)"
            />

            <div v-if="durationValue"
                 ref="dateInput"
                 :class="['base-input cursor-pointer', { 'border-none': !isDatePickerVisible }]"
                 @click="toggleDatePicker">
              <div class="display-flex align-items-center gap-16">
                <p class="compact-400-accent color-text-grey">{{ formattedSelectedDate }}</p>
                <p class="compact-400-numeric color-text-grey">{{ formattedSelectedTime }}</p>
              </div>
            </div>

            <div ref="overlay"
                 :class="['position-absolute', isDatePickerVisible ? 'user-select pointer-events': 'user-select-none pointer-events-none']"
                 class="overlay"
                 @click="toggleDatePicker">
              <div id="calendar-teleport-target"
                   ref="calendar"
                   :class="['user-select', 'pointer-events', 'position-absolute', isDatePickerVisible ? '' : 'none']"
                   :style="calendarStyle"
                   @click.stop>
                <LazyDatePicker v-if="isDatePickerVisible"
                                :disable-past-days="true"
                                :init-date="durationValue"
                                @close=toggleDatePicker
                                @update:selectedDateTime="handleSelectedDateTimeUpdate"
                />
              </div>
            </div>

          </div>
          <div class="display-flex align-items-center justify-between gap-16 fee">
            <span class="compact-400-numeric text-color-secondary">Marketplace fee</span>
            <span v-if="sumFees"
                  class="compact-400-numeric text-color-secondary"
            >{{ sumFees }} {{ symbolCurrency }}</span>
            <span v-else
                  class="compact-400-numeric text-color-secondary"
            >-</span>
          </div>
          <div v-if="values.price"
               class="display-flex align-items-center justify-between gap-16"
          >
            <span class="compact-500-accent">Total</span>
            <span v-if="sumFees && values.price"
                  class="compact-500-accent"
            >{{ totalPrice }} {{ symbolCurrency }}</span>
          </div>
        </div>
      </div>
    </div>

    <Button :is-disabled="!meta.valid"
            :is-loading="buttonLoading"
            class="width-full"
            color="bg.mojito"
            size="size.medium"
    >
      <button class="compact-500-accent display-flex justify-center gap-8 align-items-center"
              type="submit"
      >
        <span class="compact-500-accent">Make offer</span>
        <IconMain :size="24"
                  icon="arrow-r-l"
        />
      </button>
    </Button>
  </form>
</template>

<style lang="scss" scoped>
.w-balance {
  height: 24px;
}
</style>

<style lang="scss">

.price-error {
  bottom: -18px;
  color: var(--content-negative);
}

.currency-select {
  width: 100%;
  max-width: 95px;
}

.duration-select {
  width: 100%;
  max-width: 324px;
}

.fee {
  padding-top: $spacing-400;
  border-top: 1px solid var(--border-primary)
}

.block-pad {
  padding: 16px 0 24px;
}

.offer-token {
  width: 120px;
  height: 120px;
}

.wallet-offer {
  width: 52px;
  height: 52px;
  background: var(--bg-tertary);
}

.drop-input-offer {
  max-width: 324px !important;
  padding: 0 8px;
}

.btn-pad {
  padding-top: 30px;
}

.overlay {
  position: absolute;
  inset: 0;
}

.border-none {
  border: none;
}

.user-select-none {
  user-select: none;
}

.pointer-events-none {
  pointer-events: none;
}

.user-select {
  user-select: initial;
}

.pointer-events {
  pointer-events: initial;
}

.none {
  display: none;
  visibility: hidden;
  position: absolute;
  z-index: -9999;
  width: 0;
  height: 0;
  margin: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  user-select: none;
}

</style>

<style lang="scss" scoped>

.border-block {
  padding: 30px 0;
  border-top: 1px solid rgb(233 234 236 / 20%);
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

#calendar-teleport-target {
  z-index: 2;
  width: 332px;
  height: 453px;
}

.modal {
  max-height: 100%;

  &-content {
    width: calc(100% + #{$spacing-400});
    margin-right: -$spacing-400;
    padding-right: $spacing-400;
    overflow: auto;
  }

}
</style>
